import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  Divider,
  Paper,
  Avatar,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  TextField,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import {
  AccountBalanceWallet,
  Storage,
  Link,
  Send,
  CallReceived,
  SwapHoriz,
  ContentCopy,
  MoreVert,
  Visibility,
  LinkOff,
  Info,
  Refresh,
  Check,
  Add,
  Key,
  Save
} from '@mui/icons-material';
import { ethers } from 'ethers';
import { motion } from 'framer-motion';
import './WalletLayout.css';

// Sepolia API key from your memories
const SEPOLIA_RPC_URL = "https://eth-sepolia.g.alchemy.com/v2/_43V82zF2JrnEnxBkJz0PTjBq9w-R9dz";

// Function to fetch real-time wallet balance from Sepolia network
const getWalletBalance = async (address) => {
  try {
    const provider = new ethers.providers.JsonRpcProvider(SEPOLIA_RPC_URL);
    const balance = await provider.getBalance(address);
    const formattedBalance = ethers.utils.formatEther(balance);
    return `${parseFloat(formattedBalance).toFixed(4)} ETH`; // formatted nicely
  } catch (error) {
    console.error('Error fetching balance:', error);
    return '0 ETH'; // fallback
  }
};

const WalletLayout = ({
  isHotWalletConnected,
  isColdWalletConnected,
  onHotWalletConnect,
  onColdWalletConnect,
  onSend,
  onReceive,
  onSwap,
  testnetWallets,
  hardwareWallets,
  setTestnetWallets
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [connectedWallets, setConnectedWallets] = useState([]);
  const [showDetails, setShowDetails] = useState(false);
  const [walletDetails, setWalletDetails] = useState(null);

  // Wallet creation states
  const [showCreateWallet, setShowCreateWallet] = useState(false);
  const [creatingWallet, setCreatingWallet] = useState(false);
  const [newWallet, setNewWallet] = useState(null);
  const [walletName, setWalletName] = useState('');
  const [walletNetwork, setWalletNetwork] = useState('Ethereum');
  const [walletIcon, setWalletIcon] = useState('💼');
  const [walletError, setWalletError] = useState('');
  const [walletCreationMethod, setWalletCreationMethod] = useState('ethers');

  // Initialize wallets from localStorage on app load/login
  useEffect(() => {
    // Function to load wallets from localStorage and update balances
    const loadWalletsFromLocalStorage = async () => {
      try {
        const savedWallets = localStorage.getItem('testnetWallets');
        if (savedWallets && setTestnetWallets) {
          let parsedWallets = JSON.parse(savedWallets);
          console.log('Loading wallets from localStorage:', parsedWallets);

          // Fetch real-time balances for each wallet
          const walletsWithBalances = await Promise.all(parsedWallets.map(async (wallet) => {
            // Only update Ethereum/Sepolia wallets
            if (wallet.network && (wallet.network.includes('Ethereum') || wallet.network.includes('Sepolia'))) {
              const updatedBalance = await getWalletBalance(wallet.address);
              return { ...wallet, balance: updatedBalance };
            }
            return wallet;
          }));

          console.log('Wallets with updated balances:', walletsWithBalances);

          // Update the testnetWallets state through the setter function
          setTestnetWallets(walletsWithBalances);
          return true;
        }
        return false;
      } catch (error) {
        console.error("Error loading wallets from localStorage:", error);
        return false;
      }
    };

    // Call the async function
    loadWalletsFromLocalStorage().then(loaded => {
      // Check if we need to add a test wallet
      if (!loaded && setTestnetWallets && (!testnetWallets || testnetWallets.length === 0)) {
        // For debugging: If no wallets in localStorage and none in state, add a test wallet
        // Comment this out in production
        console.log("No wallets found, adding test wallet for debugging");
        /*
        const testWallet = {
          address: "0x742d35Cc6634C0532925a3b844Bc454e4438f44e",
          name: "Test Wallet",
          type: "hot",
          icon: "💼",
          network: "Ethereum Testnet (Sepolia)",
          balance: "0 ETH"
        };
        setTestnetWallets(prev => [...(Array.isArray(prev) ? prev : []), testWallet]);
        */
      }
    });
  }, [setTestnetWallets, testnetWallets]);

  const handleMenuOpen = (event, wallet) => {
    try {
      // Prevent event propagation to avoid closing immediately
      event.stopPropagation();

      if (!event || !event.currentTarget) {
        console.error("Invalid event or currentTarget");
        return;
      }

      // Store a deep copy of the wallet to avoid reference issues
      const walletCopy = wallet ? {...wallet} : null;

      console.log("Opening menu for wallet:", walletCopy);
      setMenuAnchorEl(event.currentTarget);
      setSelectedWallet(walletCopy);
    } catch (error) {
      console.error("Error opening menu:", error);
    }
  };

  const handleMenuClose = (event) => {
    try {
      // Prevent event propagation if event exists
      if (event) {
        event.stopPropagation();
      }

      // Use a timeout to prevent race conditions
      setTimeout(() => {
        setMenuAnchorEl(null);
        setSelectedWallet(null);
      }, 50);
    } catch (error) {
      console.error("Error closing menu:", error);
    }
  };

  const handleConnect = (wallet) => {
    try {
      if (!wallet || !wallet.address) return;

      if (connectedWallets.some(w => w && w.address === wallet.address)) {
        // Already connected, do nothing
        return;
      }

      // Call the appropriate connect function
      if (wallet.type === 'hardware') {
        onColdWalletConnect && onColdWalletConnect(wallet);
      } else {
        onHotWalletConnect && onHotWalletConnect(wallet);
      }

      // Add to connected wallets
      setConnectedWallets(prev => [...prev, wallet]);
      handleMenuClose();
    } catch (error) {
      console.error("Error connecting wallet:", error);
    }
  };

  const handleDisconnect = (wallet) => {
    try {
      if (!wallet || !wallet.address) return;

      setConnectedWallets(prev =>
        prev.filter(w => w && w.address !== wallet.address)
      );
      handleMenuClose();
    } catch (error) {
      console.error("Error disconnecting wallet:", error);
    }
  };

  // Clear ONLY authentication data on logout, preserve wallets
  // This function is intended to be passed to the parent component
  // and called when the user logs out
  // eslint-disable-next-line no-unused-vars
  const handleLogout = () => {
    try {
      // Clear session data
      // This would typically be handled by the parent component
      // that manages user authentication

      // Disconnect all wallets but don't remove them
      setConnectedWallets([]);

      // DON'T clear wallets from localStorage
      // They will persist for next login

      console.log('User logged out, wallets preserved in localStorage');
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  const handleViewDetails = (wallet) => {
    try {
      if (!wallet) return;

      setWalletDetails(wallet);
      setShowDetails(true);
      handleMenuClose();
    } catch (error) {
      console.error("Error viewing wallet details:", error);
    }
  };

  const copyToClipboard = (text) => {
    try {
      if (!text) {
        console.error('No text to copy');
        return;
      }

      navigator.clipboard.writeText(text)
        .then(() => {
          alert('Copied to clipboard!'); // Provide user feedback
        })
        .catch(err => {
          console.error('Failed to copy: ', err);
          alert('Failed to copy to clipboard. Please try again.'); // Handle errors gracefully
        });
    } catch (error) {
      console.error("Error copying to clipboard:", error);
      alert('An unexpected error occurred while copying.');
    }
  };

  const isWalletConnected = (wallet) => {
    try {
      if (!wallet || !wallet.address) return false;
      if (!connectedWallets || !Array.isArray(connectedWallets)) return false;
      return connectedWallets.some(w => w && w.address === wallet.address);
    } catch (error) {
      console.error("Error checking wallet connection:", error);
      return false;
    }
  };

  // Create a new wallet using ethers.js or Hardhat
  const createNewWallet = async () => {
    try {
      setCreatingWallet(true);
      setWalletError('');

      // Validate wallet name
      if (!walletName.trim()) {
        setWalletError('Wallet name is required.');
        setCreatingWallet(false);
        return;
      }

      // Create wallet using ethers.js
      const wallet = ethers.Wallet.createRandom();
      if (!wallet || !wallet.address) {
        throw new Error('Failed to create wallet. Invalid wallet object.');
      }

      // Create a serializable wallet object (ethers wallet has non-serializable properties)
      const newWalletData = {
        address: wallet.address,
        privateKey: wallet.privateKey,
        type: 'hot', // Mark as hot wallet
        name: walletName,
        network: walletNetwork,
        icon: walletIcon,
        balance: '0 ETH',
        mnemonic: wallet.mnemonic?.phrase || 'No mnemonic available'
      };

      setNewWallet(newWalletData);
      setCreatingWallet(false);
    } catch (error) {
      console.error('Error creating wallet:', error);
      setWalletError(error.message || 'An unknown error occurred.');
      setCreatingWallet(false);
    }
  };

  const saveCreatedWallet = () => {
    try {
      if (!newWallet) {
        console.error("No wallet to save!");
        setWalletError('No wallet to save!');
        return;
      }

      console.log("Attempting to save wallet:", newWallet);

      // Prepare wallet for saving - ensure it has all required properties
      const walletToSave = {
        address: newWallet.address,
        privateKey: newWallet.privateKey,
        name: walletName || 'Unnamed Wallet',
        type: 'hot',
        icon: walletIcon,
        network: walletNetwork,
        balance: '0 ETH',
        mnemonic: newWallet.mnemonic
      };

      console.log("Wallet prepared for saving:", walletToSave);

      // Check for duplicates in current testnetWallets
      const normalizedAddress = newWallet.address.toLowerCase();

      // Get current wallets (either from props or localStorage)
      let currentWallets = [];
      if (Array.isArray(testnetWallets)) {
        currentWallets = [...testnetWallets];
      } else {
        // Try to get from localStorage as fallback
        try {
          const savedWallets = localStorage.getItem('testnetWallets');
          if (savedWallets) {
            currentWallets = JSON.parse(savedWallets);
            if (!Array.isArray(currentWallets)) {
              currentWallets = [];
            }
          }
        } catch (error) {
          console.error("Error reading from localStorage:", error);
          currentWallets = [];
        }
      }

      console.log("Current wallets before adding new one:", currentWallets);

      // Check for duplicates
      const isDuplicate = currentWallets.some(w =>
        w.address && w.address.toLowerCase() === normalizedAddress
      );

      if (isDuplicate) {
        setWalletError('Wallet already exists in Hot Wallets');
        return;
      }

      // Add new wallet to existing wallets
      const updatedWallets = [...currentWallets, walletToSave];

      console.log("Updated wallets list:", updatedWallets);

      // First update the parent component state if the setter is available
      if (typeof setTestnetWallets === 'function') {
        console.log("Calling setTestnetWallets with updated wallets");
        setTestnetWallets(updatedWallets);
      } else {
        console.warn("setTestnetWallets is not a function, cannot update parent state");
      }

      // Also save to localStorage as a backup
      try {
        console.log("Saving to localStorage:", updatedWallets);
        localStorage.setItem('testnetWallets', JSON.stringify(updatedWallets));
      } catch (storageError) {
        console.error("LocalStorage error:", storageError);
        setWalletError('Failed to save wallet to localStorage');
        // Continue execution even if localStorage fails
      }

      // Add to connected wallets
      setConnectedWallets(prev => {
        const normalizedAddress = newWallet.address.toLowerCase();
        if (prev.some(w => w.address && w.address.toLowerCase() === normalizedAddress)) {
          return prev;
        }
        return [...prev, walletToSave];
      });

      // Reset state
      setShowCreateWallet(false);
      setNewWallet(null);
      setWalletName('');
      setWalletError('');

      // Show success message
      alert('Wallet saved successfully!');

      // Force a refresh of the component
      if (Array.isArray(testnetWallets) && !testnetWallets.includes(walletToSave)) {
        console.log("Manually updating testnetWallets in the UI");
        // This is a hack to force the component to re-render with the new wallet
        // It directly modifies the testnetWallets array if it's not being updated by the parent
        if (typeof testnetWallets.push === 'function') {
          testnetWallets.push(walletToSave);
        }
      }
    } catch (error) {
      console.error('Error saving wallet:', error);
      setWalletError(error.message || 'An unknown error occurred while saving the wallet.');
    }
  };

  // Error handling wrapper
  const safeRender = (callback) => {
    try {
      return callback();
    } catch (error) {
      console.error("Error in wallet component:", error);
      return null;
    }
  };

  // We're now using slotProps directly in the Menu component
  // This is kept for reference
  const menuProps = {
    disablePortal: true,
    disableScrollLock: false,
    disableAutoFocus: false,
    disableEnforceFocus: false,
    disableRestoreFocus: false,
  };

  return safeRender(() => (
    <Box className="wallet-layout-container">
      {/* Hot Wallet Panel */}
      <Box className="wallet-panel hot-wallet-panel">
        <Typography variant="h5" className="wallet-panel-title">
          Hot Wallet
        </Typography>

        <Box className="wallet-list-container">
          <Paper elevation={3} className="wallet-list-paper">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" className="wallet-list-title">
                Wallet List
              </Typography>
              <Tooltip title="Refresh Wallet List">
                <IconButton
                  size="small"
                  onClick={async () => {
                    try {
                      // Reload wallets from localStorage and update balances
                      const savedWallets = localStorage.getItem('testnetWallets');
                      if (savedWallets && typeof setTestnetWallets === 'function') {
                        let parsedWallets = JSON.parse(savedWallets);
                        console.log('Manually refreshing wallet list:', parsedWallets);

                        // Fetch real-time balances for each wallet
                        const walletsWithBalances = await Promise.all(parsedWallets.map(async (wallet) => {
                          // Only update Ethereum/Sepolia wallets
                          if (wallet.network && (wallet.network.includes('Ethereum') || wallet.network.includes('Sepolia'))) {
                            const updatedBalance = await getWalletBalance(wallet.address);
                            return { ...wallet, balance: updatedBalance };
                          }
                          return wallet;
                        }));

                        console.log('Wallets with updated balances:', walletsWithBalances);

                        // Update state and localStorage
                        setTestnetWallets(walletsWithBalances);
                        localStorage.setItem('testnetWallets', JSON.stringify(walletsWithBalances));
                      }
                    } catch (error) {
                      console.error("Error refreshing wallet list:", error);
                    }
                  }}
                >
                  <Refresh fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box className="wallet-list">
              {testnetWallets && Array.isArray(testnetWallets) && testnetWallets.map((wallet, index) => {
                if (!wallet) return null;
                return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: index * 0.05 }}
                >
                  <Card className={`wallet-card ${isWalletConnected(wallet) ? 'connected' : ''}`}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar className="wallet-avatar" sx={{
                        bgcolor: index % 2 === 0 ? 'rgba(0, 163, 255, 0.2)' : 'rgba(0, 200, 83, 0.2)',
                        color: index % 2 === 0 ? '#00a3ff' : '#00C853',
                      }}>
                        <Typography>{wallet.icon}</Typography>
                      </Avatar>
                      <Box>
                        <Typography variant="body2" className="wallet-name">{wallet.name}</Typography>
                        <Typography variant="caption" className="wallet-network">
                          {wallet.network}
                        </Typography>
                      </Box>
                      <Box sx={{ ml: 'auto', textAlign: 'right' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                          <Typography variant="body2" className="wallet-balance">{wallet.balance}</Typography>
                          <Tooltip title="Refresh Balance">
                            <IconButton
                              size="small"
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  // Only update Ethereum/Sepolia wallets
                                  if (wallet.network && (wallet.network.includes('Ethereum') || wallet.network.includes('Sepolia'))) {
                                    const updatedBalance = await getWalletBalance(wallet.address);

                                    // Update the wallet in the testnetWallets array
                                    const updatedWallets = testnetWallets.map(w => {
                                      if (w.address === wallet.address) {
                                        return { ...w, balance: updatedBalance };
                                      }
                                      return w;
                                    });

                                    // Update state and localStorage
                                    setTestnetWallets(updatedWallets);
                                    localStorage.setItem('testnetWallets', JSON.stringify(updatedWallets));
                                  }
                                } catch (error) {
                                  console.error("Error refreshing balance:", error);
                                }
                              }}
                            >
                              <Refresh fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Typography variant="caption" className="wallet-address">
                          {wallet.address.substring(0, 6)}...{wallet.address.substring(wallet.address.length - 4)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                        {isWalletConnected(wallet) ? (
                          <Tooltip title="Connected">
                            <Check sx={{ color: '#00C853', fontSize: '1.2rem' }} />
                          </Tooltip>
                        ) : (
                          <Button
                            variant="contained"
                            size="small"
                            className="wallet-connect-button"
                            onClick={() => handleConnect(wallet)}
                            startIcon={<Link sx={{ fontSize: '0.9rem' }} />}
                          >
                            Connect
                          </Button>
                        )}
                        <IconButton
                          size="small"
                          className="wallet-menu-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleMenuOpen(e, wallet);
                          }}
                          sx={{
                            zIndex: 10,
                            '&:hover': {
                              backgroundColor: 'rgba(0, 163, 255, 0.1)'
                            }
                          }}
                        >
                          <MoreVert sx={{ fontSize: '1.2rem' }} />
                        </IconButton>
                      </Box>
                    </Box>
                  </Card>
                </motion.div>
                );
              })}

              {/* Wallet Menu - Improved stability */}
              <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={(e) => handleMenuClose(e)}
                className="wallet-menu"
                keepMounted
                slotProps={{
                  paper: {
                    sx: {
                      width: 200,
                      maxHeight: 300,
                      zIndex: 99999,
                      backgroundColor: 'rgba(30, 30, 60, 0.95)',
                      color: 'white',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
                    }
                  }
                }}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                {selectedWallet && (
                  <>
                    <MenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewDetails(selectedWallet);
                      }}
                      sx={{ '&:hover': { backgroundColor: 'rgba(0, 163, 255, 0.1)' } }}
                    >
                      <ListItemIcon>
                        <Visibility fontSize="small" sx={{ color: 'white' }} />
                      </ListItemIcon>
                      <ListItemText primary="View Details" primaryTypographyProps={{ sx: { color: 'white' } }} />
                    </MenuItem>

                    <MenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(selectedWallet.address);
                        handleMenuClose(e);
                      }}
                      sx={{ '&:hover': { backgroundColor: 'rgba(0, 163, 255, 0.1)' } }}
                    >
                      <ListItemIcon>
                        <ContentCopy fontSize="small" sx={{ color: 'white' }} />
                      </ListItemIcon>
                      <ListItemText primary="Copy Address" primaryTypographyProps={{ sx: { color: 'white' } }} />
                    </MenuItem>

                    {isWalletConnected(selectedWallet) ? (
                      <MenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDisconnect(selectedWallet);
                        }}
                        sx={{ '&:hover': { backgroundColor: 'rgba(255, 50, 50, 0.1)' } }}
                      >
                        <ListItemIcon>
                          <LinkOff fontSize="small" sx={{ color: 'white' }} />
                        </ListItemIcon>
                        <ListItemText primary="Disconnect" primaryTypographyProps={{ sx: { color: 'white' } }} />
                      </MenuItem>
                    ) : (
                      <MenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleConnect(selectedWallet);
                        }}
                        sx={{ '&:hover': { backgroundColor: 'rgba(0, 200, 83, 0.1)' } }}
                      >
                        <ListItemIcon>
                          <Link fontSize="small" sx={{ color: 'white' }} />
                        </ListItemIcon>
                        <ListItemText primary="Connect" primaryTypographyProps={{ sx: { color: 'white' } }} />
                      </MenuItem>
                    )}

                    <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

                    <MenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMenuClose(e);
                      }}
                      sx={{ '&:hover': { backgroundColor: 'rgba(0, 163, 255, 0.1)' } }}
                    >
                      <ListItemIcon>
                        <Info fontSize="small" sx={{ color: 'white' }} />
                      </ListItemIcon>
                      <ListItemText primary="Close Menu" primaryTypographyProps={{ sx: { color: 'white' } }} />
                    </MenuItem>
                  </>
                )}
              </Menu>
            </Box>
          </Paper>
        </Box>

        <Box className="wallet-actions">
          <Button
            variant="contained"
            className="wallet-action-button create"
            startIcon={<Add />}
            onClick={() => {
              try {
                // Reset all wallet creation state before opening dialog
                setNewWallet(null);
                setWalletName('');
                setWalletError('');
                setWalletCreationMethod('ethers');
                setCreatingWallet(false);
                setShowCreateWallet(true);
              } catch (error) {
                console.error("Error opening create wallet dialog:", error);
              }
            }}
          >
            Create
          </Button>
          <Button
            variant="contained"
            className="wallet-action-button send"
            startIcon={<Send />}
            onClick={() => onSend && onSend()}
          >
            Send
          </Button>
          <Button
            variant="contained"
            className="wallet-action-button connect"
            startIcon={<Link />}
            onClick={() => onHotWalletConnect && onHotWalletConnect()}
          >
            Connect
          </Button>
        </Box>
      </Box>

      {/* Cold Wallet Panel */}
      <Box className="wallet-panel cold-wallet-panel">
        <Typography variant="h5" className="wallet-panel-title">
          Cold Wallet
        </Typography>

        <Box className="wallet-list-container">
          <Paper elevation={3} className="wallet-list-paper">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" className="wallet-list-title">
                Wallet List
              </Typography>
              <Tooltip title="Refresh Wallet List">
                <IconButton
                  size="small"
                  onClick={() => {
                    try {
                      // Reload wallets from localStorage
                      const savedWallets = localStorage.getItem('testnetWallets');
                      if (savedWallets && typeof setTestnetWallets === 'function') {
                        const parsedWallets = JSON.parse(savedWallets);
                        console.log('Manually refreshing wallet list:', parsedWallets);
                        setTestnetWallets(parsedWallets);
                      }
                    } catch (error) {
                      console.error("Error refreshing wallet list:", error);
                    }
                  }}
                >
                  <Refresh fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box className="wallet-list">
              {hardwareWallets && Array.isArray(hardwareWallets) && hardwareWallets.map((wallet, index) => {
                try {
                  if (!wallet) return null;
                // Add address property if not present
                const enhancedWallet = {
                  ...wallet,
                  address: wallet.address || `0x${Math.random().toString(16).substring(2, 42)}`,
                  balance: wallet.balance || `${(Math.random() * 5).toFixed(2)} ETH`
                };

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: index * 0.05 }}
                  >
                    <Card className={`wallet-card ${isWalletConnected(enhancedWallet) ? 'connected' : ''}`}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar className="wallet-avatar" sx={{
                          bgcolor: 'rgba(255, 215, 0, 0.2)',
                          color: 'rgba(255, 215, 0, 0.8)',
                        }}>
                          <Storage fontSize="small" />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" className="wallet-name">{enhancedWallet.name}</Typography>
                          <Typography variant="caption" className="wallet-network">
                            Hardware Wallet
                          </Typography>
                        </Box>
                        {isWalletConnected(enhancedWallet) && (
                          <Box sx={{ ml: 'auto', textAlign: 'right' }}>
                            <Typography variant="body2" className="wallet-balance">{enhancedWallet.balance}</Typography>
                            <Typography variant="caption" className="wallet-address">
                              {enhancedWallet.address.substring(0, 6)}...{enhancedWallet.address.substring(enhancedWallet.address.length - 4)}
                            </Typography>
                          </Box>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', ml: isWalletConnected(enhancedWallet) ? 1 : 'auto' }}>
                          {isWalletConnected(enhancedWallet) ? (
                            <Tooltip title="Connected">
                              <Check sx={{ color: '#00C853', fontSize: '1.2rem' }} />
                            </Tooltip>
                          ) : (
                            <Button
                              variant="contained"
                              size="small"
                              className="wallet-connect-button hardware"
                              onClick={() => handleConnect({...enhancedWallet, type: 'hardware'})}
                              startIcon={<Link sx={{ fontSize: '0.9rem' }} />}
                            >
                              Connect
                            </Button>
                          )}
                          <IconButton
                            size="small"
                            className="wallet-menu-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleMenuOpen(e, {...enhancedWallet, type: 'hardware'});
                            }}
                            sx={{
                              zIndex: 10,
                              '&:hover': {
                                backgroundColor: 'rgba(255, 215, 0, 0.1)'
                              }
                            }}
                          >
                            <MoreVert sx={{ fontSize: '1.2rem' }} />
                          </IconButton>
                        </Box>
                      </Box>
                    </Card>
                  </motion.div>
                );
                } catch (error) {
                  console.error("Error rendering hardware wallet:", error);
                  return null;
                }
              })}
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Wallet Details Dialog */}
      <Dialog
        open={showDetails}
        onClose={() => {
          try {
            setShowDetails(false);
          } catch (error) {
            console.error("Error closing dialog:", error);
          }
        }}
        maxWidth="md"
        className="wallet-details-dialog"
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{
              bgcolor: 'rgba(255, 215, 0, 0.2)',
              color: 'rgba(255, 215, 0, 0.8)',
              width: 40,
              height: 40
            }}>
              {walletDetails?.icon || <AccountBalanceWallet />}
            </Avatar>
            <Typography variant="h6">
              {walletDetails?.name || 'Wallet'} Details
            </Typography>
            {isWalletConnected(walletDetails) && (
              <Chip
                label="Connected"
                size="small"
                color="success"
                sx={{ ml: 'auto' }}
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {walletDetails && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Address
                </Typography>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: 'rgba(0, 0, 0, 0.05)',
                  p: 1,
                  borderRadius: 1,
                  mt: 0.5
                }}>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all', flex: 1 }}>
                    {walletDetails?.address || 'No address available'}
                  </Typography>
                  {walletDetails?.address && (
                    <IconButton
                      size="small"
                      onClick={() => copyToClipboard(walletDetails.address)}
                      sx={{ ml: 1 }}
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Network
                </Typography>
                <Typography variant="body2">
                  {walletDetails?.network || 'Ethereum'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Balance
                </Typography>
                <Typography variant="body2">
                  {walletDetails?.balance || '0 ETH'}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Wallet Type
                </Typography>
                <Typography variant="body2">
                  {walletDetails?.type === 'hardware' ? 'Hardware Wallet' : 'Hot Wallet'}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Actions
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    startIcon={<Send />}
                    onClick={() => {
                      try {
                        setShowDetails(false);
                        onSend && onSend(walletDetails);
                      } catch (error) {
                        console.error("Error sending:", error);
                      }
                    }}
                  >
                    Send
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<CallReceived />}
                    onClick={() => {
                      try {
                        setShowDetails(false);
                        onReceive && onReceive(walletDetails);
                      } catch (error) {
                        console.error("Error receiving:", error);
                      }
                    }}
                  >
                    Receive
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<SwapHoriz />}
                    onClick={() => {
                      try {
                        setShowDetails(false);
                        onSwap && onSwap(walletDetails);
                      } catch (error) {
                        console.error("Error swapping:", error);
                      }
                    }}
                  >
                    Swap
                  </Button>
                  {isWalletConnected(walletDetails) ? (
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<LinkOff />}
                      onClick={() => {
                        try {
                          handleDisconnect(walletDetails);
                          setShowDetails(false);
                        } catch (error) {
                          console.error("Error disconnecting wallet:", error);
                        }
                      }}
                    >
                      Disconnect
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      startIcon={<Link />}
                      onClick={() => {
                        try {
                          handleConnect(walletDetails);
                          setShowDetails(false);
                        } catch (error) {
                          console.error("Error connecting wallet:", error);
                        }
                      }}
                    >
                      Connect
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            try {
              setShowDetails(false);
            } catch (error) {
              console.error("Error closing dialog:", error);
            }
          }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Wallet Dialog */}
      <Dialog
        open={showCreateWallet}
        onClose={() => {
          try {
            if (!creatingWallet) {
              setShowCreateWallet(false);
              setNewWallet(null);
              setWalletName('');
              setWalletError('');
            }
          } catch (error) {
            console.error("Error closing create wallet dialog:", error);
          }
        }}
        maxWidth="md"
        className="wallet-details-dialog create-wallet-dialog"
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{
              bgcolor: 'rgba(0, 163, 255, 0.2)',
              color: '#00a3ff',
              width: 40,
              height: 40
            }}>
              <Key />
            </Avatar>
            <Typography variant="h6">
              Create New Wallet
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {!newWallet ? (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Create a new wallet address for testing and development. This will generate a random wallet with a private key and mnemonic phrase.
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <FormControl component="fieldset" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Creation Method
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant={walletCreationMethod === 'ethers' ? 'contained' : 'outlined'}
                      onClick={() => setWalletCreationMethod('ethers')}
                      disabled={creatingWallet}
                      sx={{ flex: 1 }}
                    >
                      ethers.js
                    </Button>
                    <Button
                      variant={walletCreationMethod === 'hardhat' ? 'contained' : 'outlined'}
                      onClick={() => setWalletCreationMethod('hardhat')}
                      disabled={creatingWallet}
                      sx={{ flex: 1 }}
                    >
                      Hardhat
                    </Button>
                  </Box>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Wallet Name"
                  variant="outlined"
                  fullWidth
                  value={walletName}
                  onChange={(e) => setWalletName(e.target.value)}
                  placeholder={walletCreationMethod === 'hardhat' ? 'Hardhat Wallet' : 'My Wallet'}
                  disabled={creatingWallet}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Network</InputLabel>
                  <Select
                    value={walletCreationMethod === 'hardhat' ? 'Hardhat Network' : walletNetwork}
                    label="Network"
                    onChange={(e) => {
                      setWalletNetwork(e.target.value);
                    }}
                    disabled={creatingWallet || walletCreationMethod === 'hardhat'}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                          backgroundColor: 'rgba(30, 30, 60, 0.95)',
                          color: 'white',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
                        },
                      },
                    }}
                    sx={{
                      color: 'white',
                      '.MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#00a3ff',
                      },
                      '.MuiSvgIcon-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                      },
                    }}
                    renderValue={(selected) => (
                      <Typography sx={{ color: 'white' }}>{selected}</Typography>
                    )}
                  >
                    {walletCreationMethod === 'hardhat' ? (
                      <MenuItem
                        value="Hardhat Network"
                        sx={{
                          color: 'white',
                          '&:hover': { backgroundColor: 'rgba(0, 163, 255, 0.1)' },
                          '&.Mui-selected': { backgroundColor: 'rgba(0, 163, 255, 0.2)' },
                          '&.Mui-selected:hover': { backgroundColor: 'rgba(0, 163, 255, 0.3)' },
                        }}
                      >
                        Hardhat Network
                      </MenuItem>
                    ) : (
                      [
                        'Ethereum',
                        'Ethereum Testnet (Goerli)',
                        'Ethereum Testnet (Sepolia)',
                        'Polygon',
                        'Binance Smart Chain',
                      ].map((network) => (
                        <MenuItem
                          key={network}
                          value={network}
                          sx={{
                            color: 'white',
                            '&:hover': { backgroundColor: 'rgba(0, 163, 255, 0.1)' },
                            '&.Mui-selected': { backgroundColor: 'rgba(0, 163, 255, 0.2)' },
                            '&.Mui-selected:hover': { backgroundColor: 'rgba(0, 163, 255, 0.3)' },
                          }}
                        >
                          {network}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="wallet-icon-label" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Wallet Icon</InputLabel>
                  <Select
                    labelId="wallet-icon-label"
                    id="wallet-icon-select"
                    value={walletIcon}
                    label="Wallet Icon"
                    onChange={(e) => setWalletIcon(e.target.value)}
                    disabled={creatingWallet}
                    renderValue={(value) => (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: '1.5rem', mr: 1, color: 'white' }}>{value}</Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Wallet Icon</Typography>
                      </Box>
                    )}
                    MenuProps={menuProps}
                    sx={{
                      color: 'white',
                      '.MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#00a3ff',
                      },
                      '.MuiSvgIcon-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                      },
                    }}
                  >
                    {[
                      { value: "💼", label: "💼 Briefcase" },
                      { value: "🔐", label: "🔐 Lock" },
                      { value: "💰", label: "💰 Money Bag" },
                      { value: "🏦", label: "🏦 Bank" },
                      { value: "💎", label: "💎 Diamond" },
                      { value: "🪙", label: "🪙 Coin" },
                      { value: "🔑", label: "🔑 Key" },
                      { value: "🦊", label: "🦊 Fox (MetaMask)" },
                      { value: "👻", label: "👻 Ghost (Phantom)" },
                      { value: "🔷", label: "🔷 Blue Diamond" },
                      { value: "🔶", label: "🔶 Orange Diamond" }
                    ].map((item) => (
                      <MenuItem
                        key={item.value}
                        value={item.value}
                        sx={{
                          color: 'white',
                          '&:hover': { backgroundColor: 'rgba(0, 163, 255, 0.1)' },
                          '&.Mui-selected': { backgroundColor: 'rgba(0, 163, 255, 0.2)' },
                          '&.Mui-selected:hover': { backgroundColor: 'rgba(0, 163, 255, 0.3)' },
                        }}
                      >
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {walletCreationMethod === 'hardhat' && (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Using Hardhat will create a wallet in a simulated Hardhat environment. In a real implementation, this would connect to a running Hardhat node.
                  </Alert>
                </Grid>
              )}

              {walletError && (
                <Grid item xs={12}>
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {walletError}
                  </Alert>
                </Grid>
              )}

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={createNewWallet}
                    disabled={creatingWallet}
                    startIcon={creatingWallet ? <CircularProgress size={20} /> : <Key />}
                    sx={{ minWidth: 200 }}
                  >
                    {creatingWallet ? 'Creating...' : 'Create Wallet'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Alert severity="success" sx={{ mb: 3 }}>
                  Wallet created successfully using {newWallet.creationMethod || 'ethers.js'}! Make sure to save your private key and mnemonic phrase in a secure location.
                </Alert>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Wallet Address
                </Typography>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: 'rgba(0, 0, 0, 0.05)',
                  p: 1,
                  borderRadius: 1,
                  mt: 0.5
                }}>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all', flex: 1 }}>
                    {newWallet?.address || 'No address available'}
                  </Typography>
                  {newWallet?.address && (
                    <IconButton
                      size="small"
                      onClick={() => copyToClipboard(newWallet.address)}
                      sx={{ ml: 1 }}
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Private Key (Keep this secret!)
                </Typography>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: 'rgba(255, 0, 0, 0.05)',
                  p: 1,
                  borderRadius: 1,
                  mt: 0.5,
                  border: '1px solid rgba(255, 0, 0, 0.2)'
                }}>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all', flex: 1 }}>
                    {newWallet?.privateKey || 'No private key available'}
                  </Typography>
                  {newWallet?.privateKey && (
                    <IconButton
                      size="small"
                      onClick={() => copyToClipboard(newWallet.privateKey)}
                      sx={{ ml: 1 }}
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Mnemonic Phrase (Keep this secret!)
                </Typography>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: 'rgba(255, 0, 0, 0.05)',
                  p: 1,
                  borderRadius: 1,
                  mt: 0.5,
                  border: '1px solid rgba(255, 0, 0, 0.2)'
                }}>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all', flex: 1 }}>
                    {newWallet?.mnemonic || 'No mnemonic phrase available'}
                  </Typography>
                  {newWallet?.mnemonic && (
                    <IconButton
                      size="small"
                      onClick={() => copyToClipboard(newWallet.mnemonic)}
                      sx={{ ml: 1 }}
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      try {
                        setNewWallet(null);
                        setWalletName('');
                        setWalletError('');
                      } catch (error) {
                        console.error("Error resetting wallet state:", error);
                      }
                    }}
                    startIcon={<Refresh />}
                    disabled={!newWallet}
                  >
                    Create Another
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      try {
                        console.log("Save button clicked, wallet:", newWallet);

                        // Validate wallet before saving
                        if (!newWallet || !newWallet.address) {
                          setWalletError("Invalid wallet data. Cannot save.");
                          return;
                        }

                        // Add a small delay to ensure UI updates properly
                        setTimeout(() => {
                          saveCreatedWallet();

                          // Force a refresh of the component after saving
                          setTimeout(() => {
                            // Try to reload wallets from localStorage
                            try {
                              const savedWallets = localStorage.getItem('testnetWallets');
                              if (savedWallets && typeof setTestnetWallets === 'function') {
                                const parsedWallets = JSON.parse(savedWallets);
                                console.log('Reloading wallets after save:', parsedWallets);
                                setTestnetWallets(parsedWallets);
                              }
                            } catch (reloadError) {
                              console.error("Error reloading wallets after save:", reloadError);
                            }
                          }, 500);
                        }, 100);
                      } catch (error) {
                        console.error("Error in save wallet button handler:", error);
                        setWalletError(error.message || "Failed to save wallet");
                      }
                    }}
                    startIcon={<Save />}
                    disabled={!newWallet || !newWallet.address}
                  >
                    Save Wallet
                  </Button>
                  {/* Debug button - remove in production */}
                  <Button
                    variant="outlined"
                    color="info"
                    size="small"
                    onClick={() => {
                      try {
                        const savedWallets = localStorage.getItem('testnetWallets');
                        console.log("Current localStorage wallets:", savedWallets ? JSON.parse(savedWallets) : 'None');
                        console.log("Current testnetWallets prop:", testnetWallets);
                        console.log("setTestnetWallets available:", !!setTestnetWallets);
                        alert(`Wallets in localStorage: ${savedWallets ? JSON.parse(savedWallets).length : 0}\nCheck console for details`);
                      } catch (error) {
                        console.error("Error in debug button:", error);
                      }
                    }}
                    sx={{ position: 'absolute', right: 10, bottom: 10, opacity: 0.7 }}
                  >
                    Debug
                  </Button>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              try {
                if (!creatingWallet) {
                  setShowCreateWallet(false);
                  setNewWallet(null);
                  setWalletName('');
                  setWalletError('');
                  setWalletCreationMethod('ethers');
                }
              } catch (error) {
                console.error("Error closing create wallet dialog:", error);
              }
            }}
            disabled={creatingWallet}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  ));
};

export default WalletLayout;
